import { Memoize } from '@/utils/toolkit/memoize.decorator';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, type Observable } from 'rxjs';
import type { Branding } from '../../../interfaces/Branding.interface';
import { RosaAuthService } from '../../../libs/rosa';
import { AuthProvider } from '../../../providers';
import { BrandingProvider } from '../../../providers/branding/branding.provider';
import { InformationProvider } from '../../../providers/information/information.provider';
import { RoutingService } from '../../../services/routing/routing.service';
import type { LegalDocumentsFiles } from '../general-conditions.interface';
import { AdvizeoCoreGeneralConditionsProvider } from './advizeo-core-general-conditions.service';

@Injectable({
    providedIn: 'root'
})
export class GeneralConditionsProvider {
    private _authProvider = inject(AuthProvider);
    private _informationProvider = inject(InformationProvider);
    private _advizeoCoreGeneralConditionsProvider = inject(AdvizeoCoreGeneralConditionsProvider);
    private _translateService = inject(TranslateService);
    private _brandingProvider = inject(BrandingProvider);
    private _rosaAuthService = inject(RosaAuthService);
    private _routingService = inject(RoutingService);

    branding$!: Observable<Branding>;

    async acceptGeneralConditions() {
        await firstValueFrom(this._advizeoCoreGeneralConditionsProvider.acceptGeneralConditions$(this._brandingProvider.getBranding()));
        this._rosaAuthService.refreshCurrentToken().then(() => {
            this._routingService.resetNavigationToHomePage();
        });
    }

    withdrawGeneralConditions(): Promise<void> {
        return firstValueFrom(this._advizeoCoreGeneralConditionsProvider.revokeGeneralConditions$())
            .then(() => this._authProvider.logout())
            .catch(() =>
                this._informationProvider.showToast(this._translateService.instant('GENERAL_CONDITIONS.WITHDRAW.FAILED'), 'error')
            );
    }

    @Memoize()
    getLegalDocumentFiles(): Promise<LegalDocumentsFiles> {
        return firstValueFrom(this._advizeoCoreGeneralConditionsProvider.getLegalDocumentFiles$(this._brandingProvider.getBranding()));
    }
}
